import React from "react";
import {
    DefaultButton,
    IBasePickerSuggestionsProps,
    ITag,
    PrimaryButton,
    Stack,
    TagPicker,
    Text,
} from "office-ui-fabric-react";
import { Loading } from "../../../services/Loading";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { ToastContext } from "../../common/ToastProvider";
import { Formik, FormikErrors } from "formik";
import { generateOrUpdateCustomerReport, getDCManagerApprovedDcForCustomer } from "../../../services/customers/CustomerService";
import { ICustomerReport, ICustomers } from "../../../vm";
import moment from "moment";

export interface ManageMergeDCReportDialogProps {
    handleClose: Function;
    datacenterId: string;
    customer: ICustomers;
    customerReport: ICustomerReport;
    date: Date | string;
}

const ManageMergeDCReportDialog: React.FC<ManageMergeDCReportDialogProps> = ({
    handleClose,
    datacenterId,
    customer,
    customerReport,
    date
}) => {
    //custom hooks
    const { showToast } = React.useContext(ToastContext);
    // local state
    const [isLoading, setLoading] = React.useState(false);
    const [state] = React.useState({
        selectedDcIds: [datacenterId]
    } as {
        selectedDcIds: string[]
    });
    const [datacenterOptions, setDatacenterOptions] = React.useState([] as ITag[]);

    // hooks
    React.useEffect(() => {
        const asyncFunc = async () => {
            await getDatacenterForCustomers();
        };
        asyncFunc();
    }, []);

    const getDatacenterForCustomers = async () => {
        setLoading(true);
        const result = await getDCManagerApprovedDcForCustomer({
            customerId: customer._id as string,
            date: moment(date).format("YYYY-MM-DD")
        });
        if (result?.success) {
            let datacenterOptions: ITag[] = [];

            result.data.forEach((d) => {
                datacenterOptions.push({
                    key: d._id as string,
                    name: d.name,
                });
            });
            setDatacenterOptions(datacenterOptions);
        } else {
            showToast(result?.message || "Error while getting datacenters", "error");
        }
        setLoading(false);
    };

    const handleDialogClose = (data?: ICustomerReport) => {
        handleClose(data);
    };

    const datacenterPickerSuggestionsProps: IBasePickerSuggestionsProps = {
        noResultsFoundText: "No datacenter found",
    };

    const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
        let allDataCenterList = [...datacenterOptions];

        return filterText
            ? allDataCenterList.filter(
                (tag) =>
                    tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
                    !listContainsTagList(tag, tagList)
            )
            : [];
    };

    const showAllTags = (selectedItems?: ITag[] | undefined) => {
        let allDataCenterList = [...datacenterOptions];

        return removeDuplicates(allDataCenterList, selectedItems);
    };

    function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
        return tagList.filter((tag) => !listContainsTagList(tag, tags));
    }

    const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
        if (!tagList || !tagList.length || tagList.length === 0) {
            return false;
        }
        return tagList.some((compareTag) => compareTag.key === tag.key);
    };

    return (
        <React.Fragment>
            {isLoading && <Loading />}
            <CustomDrawer
                title="Select Datacenter to Merge Report"
                handelOpen={true}
                onClose={() => handleDialogClose()}
            >
                <Stack tokens={childrenGap}>
                    <Formik
                        enableReinitialize
                        initialValues={state}
                        validate={(values: typeof state) => {
                            let errors: FormikErrors<typeof state> = {};
                            if (values.selectedDcIds.length < 1) {
                                errors.selectedDcIds = "Required";
                            }
                            return errors;
                        }}
                        onSubmit={async (values: typeof state, { setSubmitting }) => {
                            let obj = { ...values };
                            setLoading(true);
                            // todo update report
                            const tempCR = customerReport;
                            tempCR.isGenerateNewReport = true;
                            tempCR.isMerged = true;
                            tempCR.defaultDatacenterId = datacenterId;

                            const status = tempCR.datacenterStatus && tempCR.datacenterStatus.length > 0 ? tempCR.datacenterStatus[0].status : 0;
                            obj.selectedDcIds.forEach((d: string) => {
                                if (d !== datacenterId) tempCR.datacenterStatus.push({ datacenterId: d, status });
                            })

                            const result = await generateOrUpdateCustomerReport(tempCR);
                            if (result?.data) {
                                handleDialogClose(result.data);
                                showToast("Customer merge report generated successfully", "success");
                            } else {
                                showToast(
                                    result?.message || "Error while generating / updating customer report",
                                    "error"
                                );
                            }
                            setLoading(false);
                            setSubmitting(false);
                        }}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Stack wrap tokens={childrenGap}>
                                    <Stack.Item className="custom-picker">
                                        <TagPicker
                                            selectedItems={datacenterOptions.filter((d) => values.selectedDcIds.includes(d.key as string))}
                                            removeButtonAriaLabel="Remove"
                                            onResolveSuggestions={filterSuggestedTags as any}
                                            pickerSuggestionsProps={datacenterPickerSuggestionsProps}
                                            onEmptyResolveSuggestions={showAllTags}
                                            inputProps={{
                                                placeholder: "Select a particular datacenter",
                                            }}
                                            onChange={(items: any) => {
                                                console.log(values);
                                                let datacenterIds: string[] = [];
                                                items.forEach((i: any) => {
                                                    datacenterIds.push(i.key);
                                                });
                                                if (datacenterIds.includes(datacenterId)) {
                                                    setFieldValue("selectedDcIds", datacenterIds);
                                                }
                                            }}
                                        />
                                        {errors.selectedDcIds && touched.selectedDcIds && (
                                            <Text className="error-msg">{errors.selectedDcIds}</Text>
                                        )}
                                    </Stack.Item>
                                    <Stack.Item>
                                        <Stack
                                            horizontal
                                            wrap
                                            tokens={childrenGap}
                                            horizontalAlign="end"
                                        >
                                            <DefaultButton
                                                text="Cancel"
                                                onClick={() => handleDialogClose()}
                                                className="borderless-btn"
                                            />
                                            <PrimaryButton type="submit" text="Merge Report" disabled={values.selectedDcIds.length < 2} />
                                        </Stack>
                                    </Stack.Item>
                                </Stack>
                            </form>
                        )}
                    </Formik>
                </Stack>
            </CustomDrawer>
        </React.Fragment>
    );
};

export default ManageMergeDCReportDialog;